/**
 * CAD API Service — thickness analysis
 *
 * Thickness analysis creation, listing, review, presets, and comparison.
 */

import { api } from './api';
import {
  mapThicknessAnalysis,
  mapThicknessBatch,
  mapThicknessPreset,
  mapThicknessReview,
} from './cad.helpers';
import {
  ThicknessAnalysis,
  ThicknessAnalysisCreateInput,
  ThicknessAnalysisListResponse,
  ThicknessBatchJob,
  ThicknessConfig,
  ThicknessComparison,
  ThicknessPreset,
  ThicknessReview,
  CADClassification,
} from '../types/cad';

const CAD_BASE_URL = '/cad';

/**
 * Report exports are generated on demand and a large model's workbook/PDF can
 * take well past the 30s default, so the two download calls override it.
 * Declared here rather than in cad.service.ts to keep that module's re-export
 * of this one acyclic; cad.service.ts re-exports it for existing callers.
 */
export const CAD_REPORT_TIMEOUT_MS = 3 * 60 * 1000;

export const createThicknessAnalysis = async (
  modelId: string,
  input: ThicknessAnalysisCreateInput,
): Promise<ThicknessAnalysis> => {
  const data = await api.post<any>(
    `${CAD_BASE_URL}/models/${modelId}/thickness-analyses`,
    {
      lower_mm: input.lowerMm,
      upper_mm: input.upperMm,
      judgment_tolerance_mm: input.judgmentToleranceMm,
      cluster_radius_mm: input.clusterRadiusMm ?? 3,
      cluster_min_points: input.clusterMinPoints ?? 3,
      profile: input.profile ?? 'standard',
      retry_of: input.retryOf || null,
    },
  );
  return mapThicknessAnalysis(data);
};

export const listThicknessAnalyses = async (
  modelId: string,
): Promise<ThicknessAnalysisListResponse> => {
  const data = await api.get<any>(`${CAD_BASE_URL}/models/${modelId}/thickness-analyses`);
  return {
    analyses: (data.analyses || []).map(mapThicknessAnalysis),
    total: Number(data.total || 0),
    latestSuccessfulAnalysisId: data.latest_successful_analysis_id || null,
  };
};

export const getThicknessAnalysis = async (
  modelId: string,
  analysisId: string,
): Promise<ThicknessAnalysis> => {
  const data = await api.get<any>(
    `${CAD_BASE_URL}/models/${modelId}/thickness-analyses/${analysisId}`,
  );
  return mapThicknessAnalysis(data);
};

export const getThicknessGLBUrl = (modelId: string, analysisId: string): string => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  return `${baseURL}${CAD_BASE_URL}/models/${modelId}/thickness-analyses/${analysisId}/glb`;
};

export const downloadThicknessReport = async (
  modelId: string,
  analysisId: string,
  filename: string,
): Promise<void> => {
  const blob = await api.get<Blob>(
    `${CAD_BASE_URL}/models/${modelId}/thickness-analyses/${analysisId}/report.xlsx`,
    {
      responseType: 'blob',
      timeout: CAD_REPORT_TIMEOUT_MS,
    },
  );
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
};

export const downloadThicknessPdf = async (
  modelId: string,
  analysisId: string,
  filename: string,
): Promise<void> => {
  const blob = await api.get<Blob>(
    `${CAD_BASE_URL}/models/${modelId}/thickness-analyses/${analysisId}/report.pdf`,
    {
      responseType: 'blob',
      timeout: CAD_REPORT_TIMEOUT_MS,
    },
  );
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
};

export const recoverThicknessAnalysis = async (
  modelId: string,
  analysisId: string,
): Promise<ThicknessAnalysis> => {
  const data = await api.post<any>(
    `${CAD_BASE_URL}/models/${modelId}/thickness-analyses/${analysisId}/recover`,
    undefined,
  );
  return mapThicknessAnalysis(data);
};

export const getThicknessReview = async (
  modelId: string,
  analysisId: string,
): Promise<ThicknessReview> => {
  const data = await api.get<any>(
    `${CAD_BASE_URL}/models/${modelId}/thickness-analyses/${analysisId}/review`,
  );
  return mapThicknessReview(data);
};

export const updateThicknessReview = async (
  modelId: string,
  analysisId: string,
  review: Pick<
    ThicknessReview,
    'status' | 'regionDispositions' | 'regionReviews' | 'comment' | 'updatedAt'
  >,
): Promise<ThicknessReview> => {
  const data = await api.put<any>(
    `${CAD_BASE_URL}/models/${modelId}/thickness-analyses/${analysisId}/review`,
    {
      status: review.status,
      region_dispositions: review.regionDispositions,
      region_reviews: Object.fromEntries(
        Object.entries(review.regionReviews || {}).map(([regionId, item]) => [
          regionId,
          {
            analysis_confirmed: item.analysisConfirmed,
            cad_verification_mode: item.cadVerificationMode,
            cad_measured_mm: item.cadMeasuredMm,
            measurement_method: item.measurementMethod,
            disposition: item.disposition,
            rationale: item.rationale,
            assignee: item.assignee,
            due_date: item.dueDate,
          },
        ]),
      ),
      comment: review.comment,
      expected_updated_at: review.updatedAt,
    },
  );
  return mapThicknessReview(data);
};

export interface ThicknessBatchCreateInput {
  name: string;
  files: File[];
  lowerMm: number;
  upperMm: number;
  judgmentToleranceMm: number;
  clusterRadiusMm?: number;
  clusterMinPoints?: number;
  profile?: string;
  classification: CADClassification;
}

export const createThicknessBatch = async (
  input: ThicknessBatchCreateInput,
): Promise<ThicknessBatchJob> => {
  const form = new FormData();
  input.files.forEach((file) => form.append('files', file));
  form.append('name', input.name);
  form.append('lower_mm', String(input.lowerMm));
  form.append('upper_mm', String(input.upperMm));
  form.append('judgment_tolerance_mm', String(input.judgmentToleranceMm));
  form.append('cluster_radius_mm', String(input.clusterRadiusMm ?? 3));
  form.append('cluster_min_points', String(input.clusterMinPoints ?? 3));
  form.append('profile', input.profile ?? 'standard');
  form.append('folder_path', input.classification.folderPath);
  form.append('tags', JSON.stringify(input.classification.tags));
  form.append('machine_model', input.classification.machineModel);
  form.append('management_number', input.classification.managementNumber);
  const data = await api.post<any>(`${CAD_BASE_URL}/thickness-batches`, form, {
    timeout: CAD_REPORT_TIMEOUT_MS,
    headers: {
      // The shared client defaults to application/json. Override it for this
      // multipart endpoint so axios includes the files and browser boundary.
      'Content-Type': 'multipart/form-data',
    },
  });
  return mapThicknessBatch(data);
};

export const listThicknessBatches = async (): Promise<ThicknessBatchJob[]> => {
  const data = await api.get<any>(`${CAD_BASE_URL}/thickness-batches`);
  return (data.jobs || []).map(mapThicknessBatch);
};

export const getThicknessBatch = async (jobId: string): Promise<ThicknessBatchJob> => {
  const data = await api.get<any>(`${CAD_BASE_URL}/thickness-batches/${jobId}`);
  return mapThicknessBatch(data);
};

export const updateThicknessBatchClassification = async (
  job: ThicknessBatchJob,
  classification: CADClassification,
): Promise<ThicknessBatchJob> => {
  const data = await api.patch<any>(
    `${CAD_BASE_URL}/thickness-batches/${job.jobId}/classification`,
    {
      folder_path: classification.folderPath,
      tags: classification.tags,
      machine_model: classification.machineModel,
      management_number: classification.managementNumber,
      expected_updated_at: job.updatedAt,
    },
  );
  return mapThicknessBatch(data);
};

export const retryThicknessBatchItem = async (
  jobId: string,
  itemId: string,
): Promise<ThicknessBatchJob> => {
  const data = await api.post<any>(
    `${CAD_BASE_URL}/thickness-batches/${jobId}/items/${itemId}/retry`,
    undefined,
  );
  return mapThicknessBatch(data);
};

export const reanalyzeThicknessBatchItem = async (
  jobId: string,
  itemId: string,
  config: ThicknessConfig,
): Promise<ThicknessBatchJob> => {
  const data = await api.post<any>(
    `${CAD_BASE_URL}/thickness-batches/${jobId}/items/${itemId}/reanalyze`,
    {
      config: {
        lower_mm: config.lowerMm,
        upper_mm: config.upperMm,
        judgment_tolerance_mm: config.judgmentToleranceMm,
        cluster_radius_mm: config.clusterRadiusMm,
        cluster_min_points: config.clusterMinPoints,
        profile: config.profile,
      },
    },
  );
  return mapThicknessBatch(data);
};

export const downloadThicknessBatchZip = async (
  jobId: string,
  filename: string,
): Promise<void> => {
  const blob = await api.get<Blob>(
    `${CAD_BASE_URL}/thickness-batches/${jobId}/reports.zip`,
    { responseType: 'blob', timeout: CAD_REPORT_TIMEOUT_MS },
  );
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
};

export const listThicknessPresets = async (): Promise<ThicknessPreset[]> => {
  const data = await api.get<any>(`${CAD_BASE_URL}/thickness-presets`);
  return (data.presets || []).map(mapThicknessPreset);
};

export const createThicknessPreset = async (
  name: string,
  config: ThicknessConfig,
  supersedes?: string | null,
): Promise<ThicknessPreset> => {
  const data = await api.post<any>(`${CAD_BASE_URL}/thickness-presets`, {
    name,
    config: {
      lower_mm: config.lowerMm,
      upper_mm: config.upperMm,
      judgment_tolerance_mm: config.judgmentToleranceMm,
      cluster_radius_mm: config.clusterRadiusMm,
      cluster_min_points: config.clusterMinPoints,
      profile: config.profile,
    },
    supersedes: supersedes || null,
  });
  return mapThicknessPreset(data);
};

export const compareThicknessAnalyses = async (
  baseModelId: string,
  baseAnalysisId: string,
  targetModelId: string,
  targetAnalysisId: string,
): Promise<ThicknessComparison> => {
  const data = await api.post<any>(`${CAD_BASE_URL}/thickness-analyses/compare`, {
    base_model_id: baseModelId,
    base_analysis_id: baseAnalysisId,
    target_model_id: targetModelId,
    target_analysis_id: targetAnalysisId,
  });
  return {
    assessment: data.assessment,
    deltas: data.deltas || {},
    faceChanges: (data.face_changes || []).map((item: any) => ({
      sourceFaceId: item.source_face_id,
      sourceBodyId: item.source_body_id || null,
      beforeJudgment: item.before_judgment,
      afterJudgment: item.after_judgment,
      beforeMeanMm: item.before_mean_mm == null ? null : Number(item.before_mean_mm),
      afterMeanMm: item.after_mean_mm == null ? null : Number(item.after_mean_mm),
      meanDeltaMm: item.mean_delta_mm == null ? null : Number(item.mean_delta_mm),
    })),
    limitations: data.limitations || [],
  };
};
